import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { User, Users, Zap, BarChart3, ArrowRight, CheckCircle, Target, Lightbulb, Clock, DollarSign } from 'lucide-react';

const SoloServiceProUseCase = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white py-20 lg:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#c4ffa0]/20 text-[#2d4a1e] text-sm font-medium mb-8">
              <User className="w-4 h-4 mr-2" />
              Solo Service Pro Solutions
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Scale Your Service Business
              <span className="text-[#2d4a1e]"> Effortlessly</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Solo service professionals spend hours on repetitive creative tasks. 
              Our AI platform automates your marketing so you can focus on what you do best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-[#c4ffa0] to-green-400 text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                Start Free Trial
              </button>
              <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg hover:border-[#c4ffa0] hover:text-[#2d4a1e] transition-all duration-300">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-[#c4ffa0]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-400/10 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#c4ffa0]/20 text-[#2d4a1e] text-sm font-medium mb-4">
              <Lightbulb className="w-4 h-4 mr-2" />
              Core Value
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              AI-Powered Marketing Automation for Solo Professionals
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Automate your marketing creative work so you can focus on delivering exceptional service to your clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 hover:border-[#c4ffa0]/30">
              <div className="w-14 h-14 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-[#2d4a1e]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Save Hours Every Week</h3>
              <p className="text-gray-600 mb-4">
                Generate professional marketing materials in minutes instead of hours, giving you time to serve more clients.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 hover:border-[#c4ffa0]/30">
              <div className="w-14 h-14 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-[#2d4a1e]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Increase Your Revenue</h3>
              <p className="text-gray-600 mb-4">
                Professional marketing materials help you attract more clients and charge premium rates for your services.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 hover:border-[#c4ffa0]/30">
              <div className="w-14 h-14 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-[#2d4a1e]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Build Client Trust</h3>
              <p className="text-gray-600 mb-4">
                Consistent, professional branding builds credibility and helps you stand out from competitors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-20 bg-gradient-to-r from-[#c4ffa0]/5 to-green-400/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Perfect For Solo Service Professionals
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See how different types of solo professionals use our platform to grow their business
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Example 1 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                <User className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Coaches & Consultants</h3>
              <p className="text-gray-600 mb-6">
                Create professional presentations, social media content, and client materials that establish your expertise.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Professional presentations
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Social media content
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Client materials
                </li>
              </ul>
            </div>

            {/* Example 2 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-purple-500 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personal Trainers</h3>
              <p className="text-gray-600 mb-6">
                Generate workout graphics, transformation content, and client motivation materials that inspire results.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Workout graphics
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Transformation content
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Client motivation
                </li>
              </ul>
            </div>

            {/* Example 3 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Nutritionists & Dietitians</h3>
              <p className="text-gray-600 mb-6">
                Create meal planning graphics, educational content, and client communication materials that build trust.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Meal planning graphics
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Educational content
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Client communications
                </li>
              </ul>
            </div>

            {/* Example 4 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Freelancers & Creatives</h3>
              <p className="text-gray-600 mb-6">
                Generate portfolio pieces, client proposals, and marketing materials that showcase your skills.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Portfolio pieces
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Client proposals
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Marketing materials
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#c4ffa0]/10 to-green-400/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Ready to Scale Your Service Business?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of solo professionals who trust kinOS to automate their marketing and focus on what they do best.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-[#c4ffa0] to-green-400 text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center">
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button className="border-2 border-[#2d4a1e] text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#2d4a1e] hover:text-white transition-all duration-300">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SoloServiceProUseCase; 